import { format } from '@wolf/dates'
import bwipjs from 'bwip-js'
import React from 'react'
import { dayNames } from '../../utils/day-names'
import { RouterOutput } from '../../utils/trpc'
import { CompanyHeader } from '../labels/CompanyHeader'
import { PdfFrame } from '../labels/PdfFrame'

export type GeneratedDeliveryNote = RouterOutput['deliveryNotes']['generateDeliveryNotes'][number]

type Props = {
  deliveryNotes: Array<GeneratedDeliveryNote>
  onClose: () => void
}

// Above this many pages the browser needs longer to lay out the iframe before printing
const SLOW_LAYOUT_PAGE_THRESHOLD = 20

export const DeliveryNoteTemplate: React.FC<Props> = ({ deliveryNotes, onClose }) => {
  const dataMatrixImages = deliveryNotes.map(({ code }) => bwipjs
    .toCanvas(document.createElement('canvas'), { bcid: 'datamatrix', text: code, scale: 3 })
    .toDataURL('image/png'))

  return (
    <PdfFrame onClose={onClose} waitingTime={deliveryNotes.length > SLOW_LAYOUT_PAGE_THRESHOLD ? 10000 : 1000}>
      <link
        type="text/css"
        rel="Stylesheet"
        href="/styles/DeliveryNoteTemplate.css"
      />
      <link
        href="https://fonts.googleapis.com/css?family=Open+Sans"
        rel="stylesheet"
      />

      {deliveryNotes.map((deliveryNote, index) => {
        const totalUnits = deliveryNote.lines.reduce((total, { quantity }) => total + quantity, 0)

        return (
          <div className="page" key={deliveryNote.serialId}>
            <div className="top">
              <CompanyHeader />
              <div className="code">
                <img src={dataMatrixImages[index]} />
                <p>{deliveryNote.code}</p>
              </div>
            </div>

            <h2>ALBARÁN DE ENTREGA</h2>
            <p className="number">Nº {deliveryNote.serialId} · versión {deliveryNote.version}</p>

            <table className="table info">
              <tbody>
                <tr>
                  <th>Envío</th>
                  <td>{deliveryNote.deliveryGroupId}</td>
                  <th>Pedidos</th>
                  <td>{deliveryNote.orders.join(', ')}</td>
                </tr>
                <tr>
                  <th>Fecha de entrega</th>
                  <td>{format(deliveryNote.deliveryDate.start, 'dd/MM/yyyy')}</td>
                  <th>Bultos</th>
                  <td>{deliveryNote.activeBoxes}</td>
                </tr>
                <tr>
                  <th>Ruta</th>
                  <td className="highlight">{deliveryNote.route ? `${deliveryNote.route.route} - ${dayNames[deliveryNote.route.day]}` : 'Sin ruta'}</td>
                  <th>Parada</th>
                  <td className="highlight">{deliveryNote.route ? deliveryNote.route.stop : '-'}</td>
                </tr>
                <tr>
                  <th>Destinatario</th>
                  <td colSpan={3}>
                    {deliveryNote.address.firstname} {deliveryNote.address.lastname}<br />
                    {deliveryNote.address.street} {deliveryNote.address.number} {deliveryNote.address.floor}<br />
                    {deliveryNote.address.zipcode} {deliveryNote.address.city}<br />
                    Tel. {deliveryNote.address.phone}
                  </td>
                </tr>
              </tbody>
            </table>

            <table className="table lines">
              <thead>
                <tr>
                  <th className="ref">Ref.</th>
                  <th className="product">Producto</th>
                  <th>EAN</th>
                  <th className="quantity">Cantidad</th>
                </tr>
              </thead>
              <tbody>
                {deliveryNote.lines.map((line) => (
                  <tr key={line.productId}>
                    <td className="ref">{line.productId}</td>
                    <td className="product">{line.name}</td>
                    <td>{line.gs1Code ?? '-'}</td>
                    <td className="quantity">{line.quantity}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th colSpan={3} className="total-label">Total unidades</th>
                  <th className="quantity">{totalUnits}</th>
                </tr>
              </tfoot>
            </table>

            <table className="table signature">
              <tbody>
                <tr>
                  <th>Recibido por</th>
                  <th>Firma</th>
                  <th>Fecha</th>
                </tr>
                <tr>
                  <td />
                  <td />
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        )
      })}
    </PdfFrame>
  )
}
